import { useEffect, useState } from "react";

import LikeButton from "components/MessageBubble/LikeButton";
import { saveLikeDislike } from "shared/api";
import { useGlobalsContext } from "shared/hooks";
import { AppConfiguration, Literals } from "shared/types";
import classNames from "shared/utils/classNames";

import { getButtonDescriptors } from "./functions";

interface Props {
  isEndChatModalOpen: boolean;
  setIsEndChatModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetChat: () => void;
  copyTexts: Literals;
  config: AppConfiguration;
  chatId: string;
  isCloseWidgetAfterEndChat?: boolean;
  messagesLength: number;
  messageLengthWhenSkipped: number;
  setMessageLengthWhenSkipped: React.Dispatch<React.SetStateAction<number>>;
  isIframe?: boolean;
}

function EndChatModal({
  isEndChatModalOpen,
  setIsEndChatModalOpen,
  resetChat,
  copyTexts,
  config,
  chatId,
  isCloseWidgetAfterEndChat = false,
  messagesLength,
  messageLengthWhenSkipped,
  setMessageLengthWhenSkipped,
  isIframe = false,
}: Props) {
  const { setWidgetExpanded } = useGlobalsContext();
  const [isResolved, setIsResolved] = useState<boolean | null>(null);
  const [additionalFeedback, setAdditionalFeedback] = useState<string>("");

  useEffect(() => {
    if (isEndChatModalOpen && messageLengthWhenSkipped === messagesLength) {
      setWidgetExpanded(false);
    }
  }, [
    isEndChatModalOpen,
    messageLengthWhenSkipped,
    messagesLength,
    setWidgetExpanded,
  ]);

  function handleResolvedClick(isResolved: boolean) {
    setIsResolved(isResolved);
    saveLikeDislike({
      token: config?.token,
      chatId,
      likeStatus: "none", // "like" | "dislike" | "none"
      isOverallChatFeedback: true,
      isResolved,
    });
  }

  function handleSubmitClick() {
    saveLikeDislike({
      token: config?.token,
      chatId,
      likeStatus: "none", // "like" | "dislike" | "none"
      isOverallChatFeedback: true,
      additionalFeedback,
      chatEnded: true,
    });
    resetChat();
    setIsEndChatModalOpen(false);
    isCloseWidgetAfterEndChat && setWidgetExpanded(false);
    setIsResolved(null);
    setAdditionalFeedback("");
    setMessageLengthWhenSkipped(-1);
  }

  function handleSkipClick() {
    setIsEndChatModalOpen(false);
    if (isCloseWidgetAfterEndChat) {
      setWidgetExpanded(false);
      setMessageLengthWhenSkipped(messagesLength);
    } else {
      resetChat();
      setMessageLengthWhenSkipped(-1);
    }
    setIsResolved(null);
    setAdditionalFeedback("");
  }

  const buttonDescriptors = getButtonDescriptors(copyTexts);

  return (
    <>
      <div
        className={`absolute inset-0 z-[49] h-full w-full bg-gray-500/50 ${
          isEndChatModalOpen &&
          !(
            isCloseWidgetAfterEndChat &&
            messageLengthWhenSkipped === messagesLength
          )
            ? "block"
            : "hidden"
        }`}
      />
      <>
        <div
          className={classNames(
            "pointer-events-none invisible absolute inset-x-0 bottom-0 z-[50] inline-flex origin-bottom scale-y-0",
            "flex-col items-center justify-center gap-4 rounded-2xl bg-white px-4 pb-4 pt-6 opacity-0 sm:m-2 sm:h-min",
            isEndChatModalOpen &&
              !(
                isCloseWidgetAfterEndChat &&
                messageLengthWhenSkipped === messagesLength
              ) &&
              "!pointer-events-auto !visible !scale-y-100 !opacity-100",
            isIframe
              ? "left-0 top-0 m-0 h-1/2 w-1/2 transform-none sm:left-1/2 sm:top-1/2 sm:h-min" +
                  "h-52 sm:w-auto sm:-translate-x-1/2 sm:-translate-y-1/2"
              : "m-2",
          )}
        >
          <div className="text-center text-xl font-bold leading-7 text-gray-900">
            {copyTexts.endModalTitle}
          </div>

          <div className="inline-flex h-10 w-full gap-3">
            {buttonDescriptors.map(buttonDescriptor => (
              <div
                key={buttonDescriptor.label}
                className={classNames(
                  "h-full w-full cursor-pointer rounded-lg",
                  "border border-solid border-gray-200 px-2 py-3",
                  isResolved === buttonDescriptor.value
                    ? buttonDescriptor.selectedBg
                    : "bg-white",
                )}
                onClick={() => handleResolvedClick(buttonDescriptor.value)}
              >
                <div className="flex w-full justify-center gap-1 text-xs font-bold">
                  <LikeButton
                    likeStatusType={buttonDescriptor.selectedOption}
                    likeStatus={
                      isResolved === buttonDescriptor.value
                        ? buttonDescriptor.selectedOption
                        : "none"
                    }
                    className="border-none bg-inherit p-0"
                    showColored
                  />
                  <div className={buttonDescriptor.selectedText}>
                    {buttonDescriptor.label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="relative h-20 w-full">
            <textarea
              className="box-border h-full w-full resize-none rounded-lg border border-gray-200 
                bg-white p-3 text-sm font-medium leading-tight text-gray-500"
              placeholder={copyTexts.additionalFeedbackPlaceholder}
              value={additionalFeedback}
              onChange={e => setAdditionalFeedback(e?.target?.value)}
            />
          </div>
          <div className="flex w-full flex-col gap-3">
            <button
              className="btn-primary"
              onClick={() => handleSubmitClick()}
              disabled={isResolved === null && !additionalFeedback}
            >
              {copyTexts.submitButton}
            </button>
            <button className="btn-secondary" onClick={() => handleSkipClick()}>
              {copyTexts.skipFeedbackButton}
            </button>
          </div>
        </div>
      </>
    </>
  );
}

export default EndChatModal;
